import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { CircularProgress, Dialog, DialogContent } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useAddOrderItemsFrom3DMutation } from '@/app/services/orderItems'
import { useCreateOrderMutation, useGetOrderQuery } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { ORDER_STATUS_ARRAY, ORDER_USER_TYPE } from '@/common/utils/Constants/Constants'
import Unfold from '@/features/unfold-3d/Unfold'

const classes = {
    dialogRoot: {
        borderRadius: 1,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'row',
        p: 0,
    },
}

const Add3DPartsDialog = ({ is3DTube = false, onClose, open = false }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { isAuthenticated } = useKindeAuth()
    const { orderId: orderIdFromPath } = useParams()

    const organisationId = useSelector((state) => selectOrganisationId(state))
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)

    const { data: selectedOrder } = useGetOrderQuery(
        { organisationId, orderId: orderIdFromPath },
        { skip: !orderIdFromPath }
    )
    const [createOrder, { isLoading: isCreating, reset: resetCreateOrder }] = useCreateOrderMutation()
    const [addOrderItemsFrom3d, { reset: resetUploadOrderItems }] = useAddOrderItemsFrom3DMutation()

    const [isInitializing, setIsInitializing] = useState(false)
    const [orderId, setOrderId] = useState(orderIdFromPath)

    const initializeOrder = async () => {
        if (!orderId && !isInitializing) {
            setIsInitializing(true)
            const userType = isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
            const contactId = isAuthenticated ? currentContactId : isGuestLoggedIn ? guestUser.contactId : null
            const customerId = isAuthenticated ? currentCustomerId : isGuestLoggedIn ? guestUser.customerId : null

            try {
                const createOrderResponse = await createOrder({
                    organisationId,
                    payload: {
                        organisationId,
                        quoteUserType: userType,
                        customerContactId: contactId,
                        customerId: customerId,
                    },
                }).unwrap()
                setOrderId(createOrderResponse.id)
            } catch (e) {
                enqueueSnackbar(t('Failed to create order'), {
                    variant: 'error',
                })
                handleClose()
            } finally {
                setIsInitializing(false)
            }
        }
    }

    const handleClose = () => {
        onClose()
    }

    const handleAdd3DParts = async () => {
        if (orderId && selectedOrder?.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        try {
            await addOrderItemsFrom3d({ organisationId, orderId }).unwrap()

            const successMessage = is3DTube ? `${t('Rotary parts added to order')}` : `${t('3D parts added to order')}`

            enqueueSnackbar(successMessage, { variant: 'success' })
        } catch (error) {
            const errorMessage = is3DTube ? `${t('Rotary parts not added')}` : `${t('3D parts not added')}`
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        } finally {
            handleClose()
            resetUploadOrderItems()
            resetCreateOrder()
        }

        if (!orderIdFromPath) {
            navigate(`orders/${orderId}`)
        }
    }

    if (open && !orderId && !isInitializing) {
        initializeOrder()
    }

    return (
        <Dialog
            fullScreen={true}
            open={open}
            sx={classes.dialogRoot}
            onClose={handleClose}
        >
            {orderId && !isInitializing ? (
                <DialogContent sx={classes.dialogContent}>
                    <Unfold
                        disabledButtons={isCreating}
                        orderId={orderId}
                        organisationId={organisationId}
                        unfoldTube={is3DTube}
                        onCancelClicked={handleClose}
                        onSubmitClicked={handleAdd3DParts}
                    />
                </DialogContent>
            ) : (
                <DialogContent sx={classes.dialogContent}>
                    <CircularProgress />
                </DialogContent>
            )}
        </Dialog>
    )
}

Add3DPartsDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    is3DTube: PropTypes.bool,
    open: PropTypes.bool,
}

export default Add3DPartsDialog
